"use client";

import { useLang } from "@/contexts/language-context";
import { getFeaturesByVertical } from "@/lib/marketing/features-data";

/**
 * Two-row infinite marquee:
 *   Row 1 (left-to-right): every feature name from both verticals
 *   Row 2 (right-to-left): payment / integration brand names
 *
 * Adds visual movement between dense bento sections and acts as a continuity
 * ribbon — "here's everything Redy gives you" presented as a fast-scan banner.
 */
export default function FeatureMarqueeSection() {
  const { t } = useLang();

  const restaurantFeatures = getFeaturesByVertical("restaurant");
  const nightlifeFeatures = getFeaturesByVertical("nightlife");

  // Combine every feature name from both verticals; we render the list 4× to
  // guarantee the track is wider than any viewport.
  const featureNames = [...restaurantFeatures, ...nightlifeFeatures].map((f) =>
    t(f.card.title.en, f.card.title.es)
  );

  const integrations = [
    "Visa",
    "Mastercard",
    "Amex",
    "Discover",
    "Apple Pay",
    "Google Pay",
    "North",
    "Twilio SMS",
    "Resend Email",
    "Supabase",
    "iPad",
    "Android",
  ];

  return (
    <section className="relative py-20 bg-redy-warm-white overflow-hidden border-y border-black/5">
      {/* Section eyebrow */}
      <div className="text-center mb-8 px-6">
        <div className="text-[11px] font-bold tracking-widest uppercase text-redy-secondary/60">
          {t("Everything in the box", "Todo en la caja")}
        </div>
      </div>

      {/* Row 1 — features, left-to-right */}
      <MarqueeTrack
        items={featureNames}
        direction="left"
        accent="text-redy-charcoal"
        bulletColor="bg-redy-orange"
        speed={60}
      />

      {/* Row 2 — integrations, right-to-left */}
      <div className="mt-6">
        <MarqueeTrack
          items={integrations}
          direction="right"
          accent="text-redy-charcoal/45"
          bulletColor="bg-redy-charcoal/20"
          speed={50}
          smaller
        />
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MarqueeTrack — pure CSS marquee, direction + speed configurable
// ─────────────────────────────────────────────────────────────────────────────

interface MarqueeTrackProps {
  items: string[];
  direction: "left" | "right";
  accent: string;
  bulletColor: string;
  speed: number; // seconds per loop
  smaller?: boolean;
}

function MarqueeTrack({ items, direction, accent, bulletColor, speed, smaller }: MarqueeTrackProps) {
  const animationName = direction === "left" ? "marquee-left" : "marquee-right";

  return (
    <div
      className="relative overflow-hidden"
      style={{
        maskImage:
          "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)",
        WebkitMaskImage:
          "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)",
      }}
    >
      <div
        className="flex w-max gap-10 items-center hover:[animation-play-state:paused]"
        style={{
          animation: `${animationName} ${speed}s linear infinite`,
        }}
      >
        {/* Render the items 4 times so the loop is always wider than the viewport */}
        {Array.from({ length: 4 }).flatMap((_, set) =>
          items.map((label, i) => (
            <div
              key={`${set}-${i}`}
              className={`shrink-0 flex items-center gap-6 ${accent} ${
                smaller ? "text-[15px]" : "text-[22px] md:text-[28px]"
              } font-black tracking-tight`}
            >
              <span className={`shrink-0 rounded-full ${bulletColor} ${smaller ? "w-1.5 h-1.5" : "w-2 h-2"}`} />
              <span className="whitespace-nowrap">{label}</span>
            </div>
          ))
        )}
      </div>

      {/* Inline keyframes — direction-aware. Scoped via `<style jsx>` to
          avoid global pollution. */}
      <style jsx>{`
        @keyframes marquee-left {
          from { transform: translateX(0); }
          to   { transform: translateX(-25%); }
        }
        @keyframes marquee-right {
          from { transform: translateX(-25%); }
          to   { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
